import { FormattedMessage } from 'react-intl';
import { AlertCode } from 'shared/types/checkout';
import { AlertTypes, PaymentFAQsLink, SupportLink } from 'components/ui/atoms';

export const getAlertType = (key: AlertCode) =>
  ({
    redeem_discount: AlertTypes.INFO,
    remove_discount: AlertTypes.INFO,
    item_removed: AlertTypes.SUCCESS,
    currency_selected: AlertTypes.SUCCESS,
    locked_rate: AlertTypes.SUCCESS,
    autofill_user_information: AlertTypes.SUCCESS,
    quote_expired: AlertTypes.DANGER,
    rates_unavailable: AlertTypes.DANGER,
    geo_discount_applied: AlertTypes.INFO,
  })[key as string] ?? AlertTypes.DANGER;

export const getAlertMessage = (key: AlertCode, value?: string) =>
  ({
    cart_changed: (
      <FormattedMessage
        id="payment.checkout.error.cart.changed"
        defaultMessage="Your cart has changed since navigating to this page. Please reload the page and verify the product you are purchasing."
        description="Notifies the user their cart has changed, so they can reload the page to see what it actually contains."
      />
    ),
    cart_load_failed: (
      <FormattedMessage
        id="payment.checkout.error.cart.load.failed"
        defaultMessage="There was an unexpected problem while loading your cart. If the problem persists, please {supportLink}."
        description="The error message when we fail to load the cart for an unknown reason"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    fallback: (
      <FormattedMessage
        id="payment.checkout.error.fallback"
        defaultMessage="There was an unexpected problem. If the problem persists, please {supportLink}."
        description="The error message when we don't know the specific error"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    fallback_with_reload_information: (
      <FormattedMessage
        id="payment.checkout.error.reload.fallback"
        defaultMessage="There was an unexpected problem. Please reload the page. If the problem persists, please {supportLink}."
        description="The error message when we don't know the specific error"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    coupon_not_valid_for_cart: (
      <FormattedMessage
        id="payment.checkout.coupon.not.valid.for.cart"
        defaultMessage="The coupon code &lsquo;{discountCode}&rsquo; is not applicable to your current cart, please {supportLink}"
        description="Message to display when a discount code does not matches the cart"
        values={{ discountCode: value, supportLink: <SupportLink /> }}
      />
    ),
    coupon_not_eligible_for_cart: (
      <FormattedMessage
        id="payment.checkout.coupon.not.eligible.for.cart"
        defaultMessage="Cart does not qualify for coupon code &lsquo;{discountCode}&lsquo;"
        description="Message to display when a discount code does not matches the cart"
        values={{ discountCode: value }}
      />
    ),
    coupon_not_eligible_for_bundled_purchase: (
      <FormattedMessage
        id="payment.checkout.coupon.not.eligible.for.bundled.purchase"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; is not valid for this cart for a bundled purchase."
        description="Message to display when a discount code does not matches the cart for a bundled purchase"
        values={{ discountCode: value }}
      />
    ),
    coupon_already_used: (
      <FormattedMessage
        id="payment.checkout.coupon.already.used"
        defaultMessage="This code has already been used."
        description="Message to display when a discount code has already been used"
      />
    ),
    coupon_already_used_by_user: (
      <FormattedMessage
        id="payment.checkout.coupon.already.used.by.user"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; is not available. You have already used this coupon in a previous order."
        description="Message to display when a discount code has already been used by the user"
        values={{ discountCode: value }}
      />
    ),
    discount_code_expired: (
      <FormattedMessage
        id="payment.checkout.discount.code.expired"
        defaultMessage="Coupon code &lsquo;{discountCode}&lsquo; has expired."
        description="Message to display when a discount code is expired"
        values={{ discountCode: value }}
      />
    ),
    payment_processing_failed: (
      <FormattedMessage
        id="payment.checkout.error.payment.processing.failed"
        defaultMessage="There was an unexpected problem while processing your payment. If the problem persists, please {supportLink}."
        description="The error message when we fail to process the payment for an unknown reason"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    order_processing_failed: (
      <FormattedMessage
        id="payment.checkout.error.payment.processing.failed"
        defaultMessage="There was an unexpected problem while processing your order. If the problem persists, please {supportLink}."
        description="The error message when we fail to process the payment for an unknown reason"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    product_not_found: (
      <FormattedMessage
        id="payment.checkout.error.product.not.found"
        defaultMessage="Products with SKU(s) [{sku}] do not exist."
        description="Notifies the user the product they are trying to purchase is no longer available."
        values={{ sku: value }}
      />
    ),
    product_not_found_for_coupon: (
      <FormattedMessage
        id="payment.checkout.error.product.not.found.for.coupon"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; is not valid for any currently available course seats. If you need assistance, please {supportLink}."
        description="Notifies the user the coupon code they are trying to apply is not valid for any currently available course seats."
        values={{
          discountCode: value,
          supportLink: <SupportLink />,
        }}
      />
    ),
    product_entitlements_enrollment_fetch_error: null,
    product_user_me_fetch_error: (
      <FormattedMessage
        id="payment.checkout.error.user.me.fetch"
        defaultMessage="There was an error fetching your user information. Please try again."
        description="Error message when user me fetch fails"
      />
    ),
    product_user_account_profile_fetch_error: (
      <FormattedMessage
        id="payment.checkout.error.user.account.profile.fetch"
        defaultMessage="There was an error fetching your account profile. Please try again."
        description="Error message when user account profile fetch fails"
      />
    ),
    product_credit_eligibility_error: (
      <FormattedMessage
        id="payment.checkout.error.product.unavailable"
        defaultMessage="An error has occurred. We could not confirm that you are eligible for course credit. Try the transaction again."
        description="Notifies the user the eligibility for course credit could not be confirmed."
      />
    ),
    product_unavailable: (
      <FormattedMessage
        id="payment.checkout.error.product.unavailable"
        defaultMessage="The product you are trying to purchase is no longer available. If you need assistance, please {supportLink}."
        description="Notifies the user the product they are trying to purchase is no longer available."
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    redeem_discount: (
      <FormattedMessage
        id="payment.checkout.add.coupon.message"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; added to cart."
        description="Message to display when a discount code is applied to the cart"
        values={{ discountCode: value }}
      />
    ),
    remove_discount: (
      <FormattedMessage
        id="payment.checkout.remove.coupon.message"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; was removed from your cart."
        description="Message to display when a discount code is removed from the cart"
        values={{ discountCode: value }}
      />
    ),
    sku_not_provided: (
      <FormattedMessage
        id="payment.checkout.error.sku.not.provided"
        defaultMessage="SKU not provided. If you need assistance, please {supportLink}."
        description="The error message when the SKU is not provided"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    transaction_declined: (
      <FormattedMessage
        id="payment.checkout.error.transaction.declined"
        defaultMessage="Your payment could not be processed. Please check your payment information or reach out to your bank or financial institution for further assistance."
        description="Asks the user to check their information or contact their payment provider for help."
      />
    ),
    product_unavailable_to_buy: (
      <FormattedMessage
        id="payment.checkout.error.product.unavailable.to.buy"
        defaultMessage="No product is available to buy."
        description="Notifies the user the product they are trying to purchase is no longer available to buy."
      />
    ),
    product_already_enrolled: (
      <FormattedMessage
        id="payment.checkout.error.product.already.enrolled"
        defaultMessage="You have already purchased this course."
        description="Notifies the user they are already enrolled in the course they're trying to purchase."
      />
    ),
    product_already_entitled: (
      <FormattedMessage
        id="payment.checkout.error.product.already.entitled"
        defaultMessage="You have already purchased this course as part of a program."
        description="Notifies the user they already have an entitlement for the course as part of a program purchase."
      />
    ),
    order_already_placed: (
      <FormattedMessage
        id="payment.checkout.error.order.already.placed"
        defaultMessage="You have already purchased these products. If you need assistance, please {supportLink}."
        description="Notifies the user the product they are trying to purchase is already bought."
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    paypal_payment_processing_failed: (
      <FormattedMessage
        id="payment.checkout.error.paypal.payment.processing.failed"
        defaultMessage="An error has occurred with your payment. You have not been charged. Please try to submit your payment again. If this problem persists, please refer to our {paymentFAQsLink} for troubleshooting tips."
        description="The error message when we fail to process the payment for an unknown reason and ask the user to refer to the payment FAQs"
        values={{
          paymentFAQsLink: <PaymentFAQsLink />,
        }}
      />
    ),
    paypal_transaction_cancelled: (
      <FormattedMessage
        id="payment.checkout.error.paypal.transaction.cancelled"
        defaultMessage="Your transaction has been canceled. If you believe this was an error, please {supportLink}"
        description="The error message when the user cancels a transaction"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    discount_code_not_provided: (
      <FormattedMessage
        id="redeem.discount.code.not.provided"
        defaultMessage="Coupon code not provided."
        description="Message to display when a discount code is not provided"
      />
    ),
    coupon_not_found: (
      <FormattedMessage
        id="payment.checkout.coupon.not.found"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; does not exist. Please check the code and try again."
        description="Message to display when the coupon code is not found"
        values={{ discountCode: value }}
      />
    ),
    auto_promo_applied: (() => {
      const result = value?.split('/');

      return (
        <FormattedMessage
          id="redeem.discount.auto.promo.applied"
          defaultMessage={`🎉 Coupon code <coupon>${result?.[0] ?? ''}</coupon> automatically applied for <amount>${result?.[1] ?? ''} off!</amount>`}
          description="The message when a promo auto applies"
          values={{
            coupon: (coupon) => <strong>{coupon}</strong>,
            amount: (discountAmount) => <strong>{discountAmount}</strong>,
          }}
        />
      );
    })(),
    currency_selected: (
      <FormattedMessage
        id="payment.checkout.currency.selected"
        defaultMessage="Your selected currency has been applied to your order."
        description="Message to display when a new currency is applied to the cart"
      />
    ),
    item_removed: (
      <FormattedMessage
        id="payment.checkout.item.removed.message"
        defaultMessage="🎉 <boldText>{brandAndCourseName}</boldText> has been removed from your cart"
        description="Message to display when an item is removed from the cart"
        values={{
          brandAndCourseName: value,
          boldText: (text) => <strong>{text}</strong>,
        }}
      />
    ),
    sdn_hit: (
      <FormattedMessage
        id="payment.checkout.sdn.hit"
        defaultMessage="<boldText>We need additional information before processing your payment.</boldText>"
        description="Message to display when we have a sdn hit"
        values={{
          boldText: (text) => <strong>{text}</strong>,
        }}
      />
    ),
    locked_rate: (
      <FormattedMessage
        id="payment.checkout.currency.locked"
        defaultMessage="<boldText>The exchange rate in your cart from US Dollars to {currency} is valid for 24 hours.</boldText>"
        description="Message to display when the currency is changed"
        values={{
          currency: value,
          boldText: (text) => <strong>{text}</strong>,
        }}
      />
    ),
    autofill_user_information: (
      <FormattedMessage
        id="payment.checkout.autofill.user.information"
        defaultMessage="We've pre-filled your information to save you time. You can edit any field below if needed"
        description="Message to display when the user information is autofilled"
      />
    ),
    quote_expired: (
      <FormattedMessage
        id="payment.checkout.quote.expired"
        defaultMessage="Your exchange rate has expired. You may be charged a different rate once your payment is processed."
        description="Message to display when quote expires during payment"
      />
    ),
    rates_unavailable: (
      <FormattedMessage
        id="payment.checkout.rates.unavailable"
        defaultMessage="Exchange rates are currently unavailable. Your order total is displayed in US Dollars."
        description="Message to display when rates cannot be refreshed"
      />
    ),
    geo_discount_applied: (() => {
      const result = value?.split('/');

      return (
        <FormattedMessage
          id="payment.checkout.add.geo.coupon.message"
          defaultMessage={`🎉 <boldText>Congrats! You’re eligible for <amount>${result?.[1] ?? ''} off!</amount> based on your location!</boldText> Final eligibility is based on your billing location.`}
          description="The message when a promo auto applies"
          values={{
            amount: (discountAmount) => <span>{discountAmount}</span>,
            boldText: (text) => <strong>{text}</strong>,
          }}
        />
      );
    })(),
    geo_country_mismatch: (
      <FormattedMessage
        id="payment.checkout.add.geo.coupon.billing.error"
        defaultMessage="<boldText>Unfortunately, you’re not eligible for {amount} off! based on your billing address. </boldText>This discount is only available in certain locations."
        description="Message to display when the address country mismatch"
        values={{
          amount: value,
          boldText: (text) => <strong>{text}</strong>,
        }}
      />
    ),
    error_applying_geo_coupon: (
      <FormattedMessage
        id="payment.checkout.add.geo.coupon.location.error"
        defaultMessage="<boldText>Unfortunately, you are not eligible for this discount based on your location.</boldText>"
        description="Message to display when the geo coupon does not match the customer's location"
        values={{
          boldText: (text) => <strong>{text}</strong>,
        }}
      />
    ),
    geo_course_ineligible: (
      <FormattedMessage
        id="payment.checkout.add.geo.coupon.course.ineligible"
        defaultMessage="<boldText>Unfortunately, this course does not qualify for this discount.</boldText>"
        description="Message to display when the cart's course is not eligible for the geo discount"
        values={{
          boldText: (text) => <strong>{text}</strong>,
        }}
      />
    ),
    geo_program_ineligible: (
      <FormattedMessage
        id="payment.checkout.add.geo.coupon.program.ineligible"
        defaultMessage="<boldText>Unfortunately, this program does not qualify for this discount.</boldText>"
        description="Message to display when the cart's program is not eligible for the geo discount"
        values={{
          boldText: (text) => <strong>{text}</strong>,
        }}
      />
    ),
    geo_discount_apply_failed: (
      <FormattedMessage
        id="payment.checkout.add.geo.coupon.apply.failed"
        defaultMessage="<boldText>Unfortunately, this discount could not be applied.</boldText> Please try another code or proceed with your checkout."
        description="Message to display when a geo discount cannot be applied for a non-location reason"
        values={{
          boldText: (text) => <strong>{text}</strong>,
        }}
      />
    ),
    upi_payment_failed: (
      <FormattedMessage
        id="payment.checkout.error.upi.payment.failed"
        defaultMessage="There was an unexpected problem while processing your payment. Please try again or use a different payment method. If the problem persists, please {supportLink}."
        description="Message to display when a UPI payment does not complete, so the shopper can retry UPI or choose another payment method"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
  })[key];
